import { type Theme } from '@mui/material';

const compactRailWidth = { xs: 72, sm: 96 } as const;
const expandedRailWidth = { xs: 'min(296px, calc(100vw - 12px))', sm: 320 } as const;

export const managerLayoutStyles = {
  layoutRoot: (theme: Theme) => ({
    minHeight: '100vh',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
  }),
  topBar: (theme: Theme) => ({
    position: 'fixed',
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: theme.palette.divider,
    boxShadow: 'none',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    zIndex: theme.zIndex.drawer + 1,
    displayPrint: 'none',
    overflow: 'visible',
  }),
  topBarToolbar: (theme: Theme) => ({
    minHeight: { xs: 56, sm: 64 },
    px: { xs: theme.spacing(1.25), sm: theme.spacing(2) },
    backgroundColor: 'inherit',
  }),
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: '#ffffff',
  },
  topBarContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    gap: { xs: 1, sm: 2 },
  },
  topBarLeft: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    flex: '0 0 auto',
  },
  menuShell: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 1, sm: 1.5 },
    minWidth: 0,
  },
  appTitleButton: {
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 'auto',
    p: 0,
    border: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
  },
  appTitleLogo: {
    display: 'block',
    width: { xs: 56, sm: 72 },
    height: 'auto',
  },
  menuTrigger: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    minWidth: 'auto',
    px: { xs: theme.spacing(1.25), sm: theme.spacing(1.75) },
    py: { xs: theme.spacing(0.75), sm: theme.spacing(0.875) },
    borderRadius: 999,
    backgroundColor: '#eef3fb',
    color: '#111827',
    whiteSpace: 'nowrap',
    transition: theme.transitions.create(['background-color', 'color']),
    '&:hover': {
      backgroundColor: '#e2eaf8',
    },
  }),
  menuTriggerText: {
    fontSize: { xs: '0.9375rem', sm: '1rem' },
    lineHeight: 1.2,
    fontWeight: 400,
    color: 'inherit',
  },
  menuTriggerIconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuTriggerIcon: {
    fontSize: { xs: 20, sm: 22 },
    color: 'inherit',
  },
  shell: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    backgroundColor: '#ffffff',
    pt: { xs: '98px', sm: '112px' },
  },
  breadcrumbsRow: (theme: Theme) => ({
    pl: { xs: theme.spacing(11), sm: theme.spacing(15), lg: theme.spacing(16) },
    pr: { xs: theme.spacing(2), sm: theme.spacing(3), lg: theme.spacing(4) },
    py: { xs: theme.spacing(1.25), sm: theme.spacing(1.5) },
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${theme.palette.divider}`,
  }),
  pageArea: (theme: Theme) => ({
    flex: 1,
    backgroundColor: '#ffffff',
    pl: { xs: theme.spacing(9), sm: theme.spacing(12) },
  }),
  breadcrumbLink: {
    minWidth: 'auto',
    p: 0,
    border: 0,
    backgroundColor: 'transparent',
    textTransform: 'none',
    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
    fontWeight: 400,
    lineHeight: 1.4,
    color: '#111827',
    '&:hover': {
      backgroundColor: 'transparent',
      color: '#0f4c81',
    },
  },
  breadcrumbText: {
    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
    fontWeight: 400,
    lineHeight: 1.4,
    color: '#111827',
  },
  currentBreadcrumb: {
    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
    fontWeight: 400,
    lineHeight: 1.4,
    color: '#97a3b6',
  },
  breadcrumbSeparator: {
    color: '#9aa4b2',
    fontSize: 18,
  },
  breadcrumbs: {
    '& .MuiBreadcrumbs-ol': {
      alignItems: 'center',
      flexWrap: 'wrap',
      rowGap: 0.5,
    },
  },
  menuDock: (theme: Theme) => ({
    position: 'fixed',
    top: { xs: 57, sm: 65 },
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.drawer + 2,
    pointerEvents: 'none',
  }),
  menuRail: (open: boolean) => (theme: Theme) => ({
    borderRadius: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.12)',
    overflow: open ? 'hidden' : 'visible',
    height: '100%',
    width: open ? expandedRailWidth : compactRailWidth,
    minWidth: open ? expandedRailWidth : compactRailWidth,
    maxWidth: '100vw',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: open ? '#ffffff' : '#f8fafc',
    pointerEvents: 'auto',
    transition: theme.transitions.create(['width', 'min-width', 'background-color'], {
      duration: theme.transitions.duration.enteringScreen,
      easing: theme.transitions.easing.easeInOut,
    }),
  }),
  menuCompactBody: (theme: Theme) => ({
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'visible',
    overscrollBehavior: 'contain',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.divider} transparent`,
    py: { xs: theme.spacing(1), sm: theme.spacing(1.25) },
    '&::-webkit-scrollbar': {
      width: 4,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.divider,
      borderRadius: 999,
    },
  }),
  menuCompactList: {
    py: 0,
  },
  menuCompactGroupWrap: {
    position: 'relative',
  },
  menuCompactLabelRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 18,
  },
  menuCompactItemButton: (isActive: boolean) => ({
    minHeight: { xs: 74, sm: 86 },
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.75, sm: 1 },
    mx: { xs: 0.5, sm: 0.75 },
    my: 0.25,
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    backgroundColor: isActive ? '#e8f0fc' : 'transparent',
  }),
  menuCompactBadge: (isActive: boolean) => ({
    position: 'relative',
    width: { xs: 24, sm: 28 },
    height: { xs: 24, sm: 28 },
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: isActive ? '#0f4c81' : '#6b7280',
  }),
  menuCompactIcon: (isActive: boolean) => ({
    fontSize: { xs: 22, sm: 24 },
    color: isActive ? '#0f4c81' : '#6b7280',
  }),
  menuCompactLabel: (isActive: boolean) => ({
    width: '100%',
    fontSize: { xs: '0.68rem', sm: '0.74rem' },
    lineHeight: 1.2,
    fontWeight: isActive ? 600 : 500,
    color: isActive ? '#0f4c81' : '#111827',
    textAlign: 'center',
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  }),
  menuCompactGroupArrow: (isActive: boolean) => ({
    position: 'absolute',
    right: { xs: -7, sm: -8 },
    bottom: { xs: -5, sm: -6 },
    fontSize: { xs: 12, sm: 14 },
    color: isActive ? '#0f4c81' : '#6b7280',
    backgroundColor: '#f8fafc',
    borderRadius: 999,
  }),
  compactGroupFlyout: (theme: Theme) => ({
    position: 'absolute',
    top: 0,
    left: 'calc(100% - 4px)',
    zIndex: 1,
    minWidth: { xs: 220, sm: 272 },
    maxWidth: { xs: 'min(280px, calc(100vw - 88px))', sm: 320 },
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#ffffff',
    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.12)',
    overflow: 'hidden',
  }),
  compactGroupFlyoutTitle: (theme: Theme) => ({
    px: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    py: { xs: theme.spacing(1.25), sm: theme.spacing(1.5) },
    fontSize: { xs: '0.875rem', sm: '0.95rem' },
    lineHeight: 1.3,
    fontWeight: 700,
    color: '#111827',
    borderBottom: `1px solid ${theme.palette.divider}`,
  }),
  compactGroupFlyoutList: (theme: Theme) => ({
    py: theme.spacing(0.75),
  }),
  compactGroupFlyoutItem: (isActive: boolean) => ({
    minHeight: { xs: 40, sm: 44 },
    alignItems: 'flex-start',
    mx: 0.75,
    my: 0.125,
    px: { xs: 1.25, sm: 1.5 },
    py: 1,
    borderRadius: 2,
    backgroundColor: isActive ? '#eef3fb' : 'transparent',
  }),
  compactGroupFlyoutItemText: (isActive: boolean) => ({
    fontSize: { xs: '0.875rem', sm: '0.94rem' },
    lineHeight: 1.35,
    fontWeight: isActive ? 600 : 400,
    color: isActive ? '#0f4c81' : '#374151',
    whiteSpace: 'normal',
  }),
  menuPanelBody: (theme: Theme) => ({
    flex: 1,
    overflowY: 'auto',
    py: { xs: theme.spacing(1), sm: theme.spacing(1.5) },
  }),
  menuFooter: (theme: Theme) => ({
    padding: { xs: theme.spacing(1), sm: theme.spacing(1.5) },
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#ffffff',
  }),
  menuList: {
    py: 0,
  },
  menuItemButton: (isActive: boolean) => ({
    minHeight: { xs: 50, sm: 56 },
    alignItems: 'center',
    mx: { xs: 1, sm: 1.5 },
    my: 0.25,
    px: { xs: 1.5, sm: 2 },
    py: { xs: 1, sm: 1.25 },
    borderRadius: 2,
    backgroundColor: isActive ? '#eef3fb' : 'transparent',
  }),
  menuItemContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  menuItemLeading: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
  },
  menuItemIconWrap: (isActive: boolean) => ({
    width: 22,
    height: 22,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: isActive ? '#0f4c81' : '#6b7280',
  }),
  menuItemIcon: (isActive: boolean) => ({
    fontSize: 20,
    color: isActive ? '#0f4c81' : '#6b7280',
  }),
  menuItemLabelWrap: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  menuItemText: (isActive: boolean) => ({
    flex: 1,
    fontSize: { xs: '0.9375rem', sm: '0.975rem' },
    lineHeight: 1.35,
    fontWeight: isActive ? 600 : 400,
    color: isActive ? '#0f4c81' : '#111827',
    whiteSpace: 'normal',
  }),
  menuItemArrow: {
    marginLeft: 'auto',
    marginTop: 1,
    color: '#6b7280',
    fontSize: 22,
  },
  menuNestedList: (theme: Theme) => ({
    pb: theme.spacing(0.5),
  }),
  menuNestedItemButton: (isActive: boolean) => ({
    minHeight: { xs: 40, sm: 44 },
    alignItems: 'flex-start',
    mx: { xs: 1, sm: 1.5 },
    my: 0.125,
    pl: { xs: 2.5, sm: 3.5 },
    pr: { xs: 1.5, sm: 2 },
    py: 1,
    borderRadius: 2,
    backgroundColor: isActive ? '#eef3fb' : 'transparent',
  }),
  menuNestedItemText: (isActive: boolean) => ({
    fontSize: { xs: '0.875rem', sm: '0.94rem' },
    lineHeight: 1.35,
    fontWeight: isActive ? 600 : 400,
    color: isActive ? '#0f4c81' : '#374151',
    whiteSpace: 'normal',
  }),
  logoutButton: {
    minHeight: 48,
    justifyContent: 'center',
  },
  logoutButtonText: {
    fontSize: '0.975rem',
    lineHeight: 1.35,
    fontWeight: 600,
    color: '#111827',
  },
  compactFooter: (theme: Theme) => ({
    flexShrink: 0,
    padding: { xs: theme.spacing(0.75), sm: theme.spacing(1) },
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#f8fafc',
  }),
} as const;

export const guaranteesLayoutStyles = managerLayoutStyles;
